import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot antispam
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    // Client-side honeypot check
    if (formData.website_url) {
      // Bot detected, pretend success
      setStatus('success');
      return;
    }

    try {
      const endpoint = 'https://api.dev.auxo.dk/webform/submit';
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setErrorMessage(errorData.message || 'Der opstod en fejl ved afsendelsen. Prøv venligst igen eller ring på tlf. 29 92 18 79.');
        setStatus('error');
      }
    } catch (err) {
      setErrorMessage('Der opstod en netværksfejl. Du er meget velkommen til at kontakte Marie direkte på tlf. 29 92 18 79 eller mail kontakt@dinprivatpsykolog.dk.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-[#EBF2EE] border border-[#C2D6C9] rounded-none p-8 text-center space-y-4">
        <div className="w-16 h-16 rounded-none bg-[#5f7365] text-white flex items-center justify-center mx-auto text-2xl shadow-sm">
          <i className="fa-regular fa-check" aria-hidden="true" />
        </div>
        <h3 className="font-serif text-2xl font-bold text-[#5f7365]">
          Mange tak for din henvendelse
        </h3>
        <p className="text-[#3B4D42] max-w-md mx-auto leading-relaxed">
          Din besked er nu sendt til Marie. Du kan forvente at få svar hurtigst muligt.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-[#5f7365] text-white font-medium rounded-none hover:bg-[#4e6053] transition-all duration-200 hover:-translate-y-1 text-sm"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-[#EDE6D8] rounded-none p-6 sm:p-8 shadow-xs space-y-6">
      <div className="space-y-1">
        <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#5f7365]">
          Send en fortrolig besked
        </h3>
        <p className="text-xs sm:text-sm text-[#576B5F]">
          Udfyld formularen herunder for at høre mere eller aftale tid til terapi.
        </p>
      </div>

      {/* Honeypot field - ABSOLUTELY HIDDEN FROM HUMANS */}
      <div className="sr-only absolute" aria-hidden="true">
        <label htmlFor="website_url">Website URL (leave blank)</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          tabIndex="-1"
          autoComplete="off"
          value={formData.website_url}
          onChange={handleChange}
        />
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-[#5f7365] mb-1">
            Navn <span className="text-rose-600">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            placeholder="Dit fulde navn"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-none border border-[#D5CEBF] bg-white text-[#5f7365] focus:outline-none focus:ring-2 focus:ring-[#5f7365] focus:border-transparent transition-all text-sm"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-[#5f7365] mb-1">
              Telefonnummer
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              placeholder="f.eks. 12 34 56 78"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-none border border-[#D5CEBF] bg-white text-[#5f7365] focus:outline-none focus:ring-2 focus:ring-[#5f7365] focus:border-transparent transition-all text-sm"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[#5f7365] mb-1">
              E-mail <span className="text-rose-600">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              placeholder="din@email.dk"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-none border border-[#D5CEBF] bg-white text-[#5f7365] focus:outline-none focus:ring-2 focus:ring-[#5f7365] focus:border-transparent transition-all text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-[#5f7365] mb-1">
            Beskrivelse / Besked <span className="text-rose-600">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            rows={5}
            placeholder="Skriv kort hvad du ønsker hjælp til eller har spørgsmål om..."
            value={formData.message}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-none border border-[#D5CEBF] bg-white text-[#5f7365] focus:outline-none focus:ring-2 focus:ring-[#5f7365] focus:border-transparent transition-all text-sm resize-y"
          />
        </div>
      </div>

      {status === 'error' && (
        <div className="p-4 rounded-none bg-rose-50 border border-rose-200 text-rose-800 text-xs sm:text-sm flex items-start gap-2">
          <i className="fa-regular fa-circle-exclamation mt-0.5 shrink-0" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full py-3.5 px-6 bg-[#5f7365] hover:bg-[#4e6053] text-white font-semibold rounded-none shadow-sm transition-all duration-200 hover:-translate-y-1 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5f7365] disabled:opacity-50"
      >
        {status === 'submitting' ? (
          <>
            <i className="fa-regular fa-spinner fa-spin" aria-hidden="true" />
            <span>Sender besked...</span>
          </>
        ) : (
          <>
            <i className="fa-regular fa-paper-plane" aria-hidden="true" />
            <span>Send besked</span>
          </>
        )}
      </button>

      <p className="text-[11px] text-[#788B7E] text-center">
        Dine oplysninger behandles med fuld fortrolighed og tavshedspligt i overensstemmelse med GDPR.
      </p>
    </form>
  );
}
